import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import type { MailLinkDto } from "../../../services/mailLinkService";
import type { CallLogDto } from "../../../services/telephonyService";
import type { DocumentDto } from "../../../services/documentService";
import type { VorgangTerminDto } from "../../../services/calendarTerminService";
import type { VorgangEntwurf } from "../../../services/graphMailService";
import type { VerlaufEintrag } from "../v3/verlauf";
import { ereignisKennung, ereignisseAus } from "./ereignisBloecke";
import { htmlZuKlartext } from "./klartext";
import { istKommentar, zielSchluessel, zielVon } from "../../notizbloecke/kommentarModell";
import type { ListenZeile } from "./AlleListeDialog";

/**
 * Die Zeilen des ARCHIVS — reine Ableitung, bewusst ohne React und ohne Fetch
 * (vitest `environment: node`, s. archivZeilen.test.ts).
 *
 * ══ WOZU ═══════════════════════════════════════════════════════════════════
 * Das Archiv (PO 11.09.2026) ist die Suchfläche über GENAU DIESEN Vorgang:
 * Mails, Anrufe, Dokumente und Notizen, quer über alle To-dos, in einer Liste.
 * „Ich kann ja 20 To-dos da drin haben, dann wenn ich nicht weiß, in welchem
 * To-do das ist" (11.09.2026, 10:22) — darum trägt jede Zeile in `todoTitel`,
 * woher sie stammt.
 *
 * Es ist NICHT der Verlauf und NICHT das Kundentagebuch (10:40–11:04): der
 * Notizstrom bleibt, wie er ist; hier wird nur nachgeschlagen.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DIE FALLE ══════════════════════════════════════════════════════════════
 * `baueVerlauf` wertet Notizen seit PO 08.09.2026 NICHT mehr aus — das steht
 * im Typ-Kommentar von `VerlaufQuellen`. Wer hier nur `eintraege` durchreicht,
 * bekommt eine Liste ohne Notizen, ohne dass irgendwo etwas bricht. Deshalb
 * nimmt `archivZeilen` die Notizen SEPARAT entgegen und mischt sie selbst dazu.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type ArchivArt = "mail" | "anruf" | "dokument" | "termin" | "notiz" | "kommentar";

/**
 * Eine Zeile des Archivs — eine {@link ListenZeile} plus das, was erst die
 * gemeinsame Liste braucht.
 *
 * `ikone` und `onOeffnen` fehlen mit Absicht: Das Icon einer Mail ist der
 * Avatar ihres Absenders, und „öffnen" heißt je nach Art Mail-Fenster oder
 * Dokumenten-Viewer — beides ist React und gehört in {@link ArchivAbschnitt},
 * nicht in eine Ableitung, die im Node-Test läuft. Der Abschnitt setzt sie
 * beim Rendern aus `art` und der mitgeführten Nutzlast.
 */
export interface ArchivZeile extends Omit<ListenZeile, "ikone" | "onOeffnen"> {
    art: ArchivArt;
    /** Zeitpunkt — Sortierschlüssel; die Anzeige steht in `wann`. */
    at: Date;
    /*
     * ══ DIE KARTE (PO 18.09.2026) ══════════════════════════════════════════
     * Der Verlauf ist seit dem 18.09.2026 keine Rasterzeile mehr, sondern eine
     * Karte mit leisem Kopf: „[Art] Wer · Kennung … wann" und darunter der
     * Inhalt. `titel` und `unterzeile` bleiben daneben bestehen — sie tragen
     * die Zeilenfassung (`ListenZeile`), und sie sind es auch, wonach gesucht
     * wird.
     */
    /** Fett im Kartenkopf: Absender, Gesprächspartner, Kunde. */
    wer: string;
    /**
     * Leise daneben: Betreff der Mail, Richtung·Status·Dauer des Anrufs,
     * Typ·Größe des Dokuments — beim Dokument ausdrücklich OHNE den
     * Dateinamen, der steht in der Karte darunter (PO 18.09.2026: „beim
     * Dokument im Header den Dateinamen raus"). Leer ist erlaubt.
     */
    kennung: string;
    /**
     * Was in der Karte steht: der Mail-Anriss, die Gesprächsnotiz, der
     * Dateiname, der Auszug der Termin-Notiz, der Notiztext. Fehlt er, bleibt
     * die Karte weg und nur der Kopf steht da — ein leerer weißer Kasten ist
     * schlimmer als keiner.
     */
    auszug?: string;
    /**
     * NUR an Notiz und Kommentar: der Benutzerschlüssel des Verfassers. Die
     * Auflösung zum Namen braucht die Benutzerliste und damit React-Kontext —
     * sie geschieht beim Zeichnen, nicht hier.
     */
    autorKey?: string;
    /** Zugehöriges To-do; null = hängt direkt am Vorgang. */
    todoId: number | null;
    /** Betreff des To-dos, aus dem die Zeile stammt; null = keins. */
    todoTitel: string | null;
    /** Nutzlast — genau eine ist gesetzt, welche sagt `art`. */
    mail?: MailLinkDto;
    /** Übrige Mails derselben Unterhaltung, jüngste zuerst (`VerlaufEintrag.unterhaltung`). */
    unterhaltung?: MailLinkDto[];
    anruf?: CallLogDto;
    dokument?: DocumentDto;
    /** Frühere Fassungen des Dokuments, jüngste zuerst (`VerlaufEintrag.fassungen`). */
    fassungen?: DocumentDto[];
    /** Termin mit Notiz-Seite (PO 14.09.2026) — öffnen heißt: zur Termin-Seite. */
    termin?: VorgangTerminDto;
    notiz?: AggregatedNoteDto;
    /**
     * Ein MAIL-ENTWURF (User 22.10.2026: „Mail-Entwurf — sowie Mail im
     * Verlauf mit Kommentar anzeigen"). Art `mail`, damit er aussieht und
     * filtert wie eine Mail; die Karte setzt die Marke „Entwurf" und öffnet
     * ihn im Verfassen-Fenster statt im Lesefenster.
     */
    entwurf?: VorgangEntwurf;
}

export interface ArchivQuellen {
    /** Mails, Anrufe, Dokumente und Termine des GANZEN Vorgangs (nicht `eigeneEintraege`). */
    eintraege: VerlaufEintrag[];
    /** Die Notizen — siehe „DIE FALLE" oben. */
    notizen: AggregatedNoteDto[];
    /**
     * Die To-dos des Vorgangs, nur für die Herkunftsangabe an Mail, Anruf und
     * Dokument. Optional, weil ein Eintrag ohne sie lediglich seinen
     * To-do-Namen einbüßt — die Notizen bringen ihren eigenen mit (`todoTitel`).
     */
    todos?: Array<{ id: number; titel: string }>;
    /**
     * Kommentare als EIGENE Art führen (PO 11.09.2026: „Nur dann muss ich nach
     * dem Ding auch suchen können").
     *
     * Ein Kommentar ist eine Notiz mit Bezug (`kommentarModell.ts`). Ohne den
     * Schalter zählt er wie bisher als „notiz" — exakt das heutige Ergebnis.
     * Mit ihm bekommt er die Art `"kommentar"`, seine Unterzeile nennt den
     * Bezug, und der Suchtext trägt den Titel des kommentierten Elements.
     *
     * Er ist Pflicht und keine Vorgabe: der Aufrufer muss sich entscheiden.
     * In V4 wandern die Kommentare aus dem Strom hinter eine Sprechblase, und
     * das Archiv ist der Weg zurück zu ihnen. (Bis zum 20.09.2026 speiste
     * dieselbe Liste auch V6, das die Kommentare im Strom behielt.)
     */
    mitKommentaren?: boolean;
    /**
     * NOTIZEN BLEIBEN DRAUSSEN (PO 28.09.2026: „Notizen nicht im Archiv").
     *
     * Seit die Notizen ein fortlaufendes Feld sind, das in jeder Karte offen
     * steht, stand jede Notiz zweimal auf der Seite — einmal zum Schreiben,
     * einmal als Archivzeile darunter („Notiz schreiben macht der doppelt").
     * Das Archiv trägt seitdem nur, was NICHT schon als Text dasteht: Mails,
     * Anrufe, Dokumente, Termine — und die Kommentare, die hinter ihrer
     * Sprechblase verschwinden und nur hier über alle To-dos zu finden sind.
     * Die Titel der Notizen bleiben Suchgut der Kommentarzeilen („Kommentar
     * zu: …").
     */
    ohneNotizen?: boolean;
}

/**
 * Das Datum einer Archivzeile — auf TAGESEBENE, mit Wochentag: „heute",
 * „Mi., 10.09.", bei älterem Jahrgang „Mi., 10.09.2025".
 *
 * ══ EIGENE FORMATIERUNG, KEIN UMBAU VON `datumKurz` ════════════════════════
 * `datumKurz` aus `v3/verlauf.ts` schreibt „10.09. 14:30" und hängt an jedem
 * Verlauf der Anwendung. Im Archiv steht die Uhrzeit im Weg: die Spalte ist
 * 92 px schmal, und wer nachschlägt, sucht den TAG („war das nicht Mittwoch?"),
 * nicht die Minute. Der Wochentag ist genau das, was das Erinnern trägt —
 * darum steht er vorn.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Wochentage stehen als Tabelle da und kommen nicht aus `toLocaleDateString`:
 * deren Kürzel hängen an der ICU-Fassung der Laufzeit („Mi." oder „Mi", je nach
 * CLDR-Stand) — in einer Spalte mit Tabellenziffern fiele das auf.
 */
const WOCHENTAGE = ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."] as const;

const selberTag = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

export function archivDatum(d: Date, jetzt: Date = new Date()): string {
    // Die Epoche ist das Zeichen für „kein Datum" — siehe `zeit()` unten.
    if (!d || Number.isNaN(d.getTime()) || d.getTime() === 0) return "ohne Datum";
    if (selberTag(d, jetzt)) return "heute";
    const tag = WOCHENTAGE[d.getDay()];
    const tagMonat = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
    // Das laufende Jahr bleibt ungenannt: es steht sonst zwanzigmal
    // untereinander und sagt nichts.
    return d.getFullYear() === jetzt.getFullYear()
        ? `${tag}, ${tagMonat}`
        : `${tag}, ${tagMonat}${d.getFullYear()}`;
}

/**
 * Die Zeile unter der Überschrift: „34 Einträge aus 3 To-dos".
 *
 * `todos` sind ALLE To-dos des Vorgangs, nicht nur die mit Einträgen
 * (PO 13.09.2026). Der Satz beantwortet die Frage „wie viel steckt in diesem
 * Vorgang" — ein To-do ohne Eintrag gehört genauso dazu, und eine Zahl, die
 * beim Anlegen eines leeren To-dos gleich bleibt, wäre nicht erklärbar.
 * Ein Vorgang ohne To-dos nennt nur die Einträge; „aus 0 To-dos" wäre Lärm.
 */
export function archivKopfText(eintraege: number, todos: number): string {
    const e = `${eintraege} ${eintraege === 1 ? "Eintrag" : "Einträge"}`;
    if (todos <= 0) return e;
    return `${e} aus ${todos} ${todos === 1 ? "To-do" : "To-dos"}`;
}

/** ISO-String → Date; Unlesbares und Leeres wird zur Epoche (= „ohne Datum"). */
function zeit(iso?: string | null): Date {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

/**
 * Notizblöcke werden als HTML gespeichert (`format: 'html'`). Vor dem
 * Durchsuchen müssen die Tags weg — sonst fände eine Suche nach „strong" jede
 * fett gesetzte Notiz, und der Zeilentitel begänne mit „<p>".
 *
 * Bewusst ohne DOM: dasselbe Vorgehen wie `istLeer` in `blockHtml.ts`, damit
 * das Modul im Node-Test läuft.
 *
 * Ein Bild (PO 14.09.2026) wird zu „[Bild]": ein Block, der nur ein Bild
 * trägt, hat sonst keinen Klartext — er fiele aus dem Archiv, obwohl er im
 * Strom steht (dieselbe Regel wie `istLeererBlocktext`). Der Linktext eines
 * `<a>` bleibt wie jeder Text stehen.
 */
export function notizKlartext(text: string, format?: "text" | "html" | null): string {
    if (format !== "html") return (text ?? "").replace(/\s+/g, " ").trim();
    // Der Tag-Strip liegt in `klartext.ts`: die Termin-Karte braucht ihn auch,
    // und zwei Fassungen liefen auseinander.
    return htmlZuKlartext(text);
}

/** Erste `max` Zeichen, an einer Wortgrenze abgeschnitten; leer bleibt leer. */
function kuerze(s: string, max: number): string {
    if (s.length <= max) return s;
    const schnitt = s.slice(0, max);
    const luecke = schnitt.lastIndexOf(" ");
    return `${(luecke > max * 0.6 ? schnitt.slice(0, luecke) : schnitt).trimEnd()} …`;
}

/**
 * Der Titel des kommentierten Elements — „Kommentar zu: ‹…›".
 *
 * Ist der Bezug nicht (mehr) in den Quellen (gelöschte Mail, leergeräumte
 * Elternnotiz), bleibt er leer: die Zeile sagt dann nur „Kommentar". Sie
 * VERSCHWINDET nicht — ein Kommentar ohne auffindbaren Bezug ist genau der,
 * den man im Archiv sucht.
 */
function bezugTitel(n: AggregatedNoteDto, titelJeZiel: Map<string, string>): string {
    const ziel = zielVon(n);
    return ziel ? (titelJeZiel.get(zielSchluessel(ziel)) ?? "") : "";
}

function ausNotiz(
    n: AggregatedNoteDto,
    opt: { mitKommentaren?: boolean; titelJeZiel: Map<string, string> },
): ArchivZeile {
    const klartext = notizKlartext(n.text, n.format);
    // Erste Zeile als Titel, der Rest als Unterzeile — wie man eine Notiz
    // überfliegt. Dass hier etwas steht, sichert `archivZeilen`: inhaltsleere
    // Notizen kommen gar nicht erst in die Liste.
    const titel = kuerze(klartext, 90);
    const rest = klartext.length > 90 ? kuerze(klartext.slice(90).trim(), 120) : "";
    const at = zeit(n.timestamp);
    const alsKommentar = Boolean(opt.mitKommentaren) && istKommentar(n);
    const bezug = alsKommentar ? bezugTitel(n, opt.titelJeZiel) : "";
    return {
        // `source` MUSS in den Schlüssel: Die Liste kommt aus
        // `vorgangNoteService.listAggregated`, das VorgangNote und TodoNote
        // zusammenmischt — zwei Tabellen mit eigenen Id-Folgen, also gibt es
        // `vorgang:5` und `todo:5` nebeneinander. Genau darum schlüsselt das
        // Repo Notizen überall zusammengesetzt (`blockModell.ts`, und das
        // Backend sichert es in `vorgang-note-aggregate.spec.ts` ab). Ohne
        // `source` stünde hier zweimal `notiz-5`: React dürfte bei doppeltem
        // `key` eine der beiden Zeilen auslassen, und der Gleichstands-
        // Tie-Break unten (`localeCompare`) käme auf 0.
        key: `notiz-${n.source ?? "vorgang"}-${n.id}`,
        art: alsKommentar ? "kommentar" : "notiz",
        at,
        titel,
        // Die Karte: der Verfasser im Kopf, der ganze Text darunter. Beim
        // Kommentar nennt die Kennung, wozu er gehört.
        wer: "",
        kennung: alsKommentar ? (bezug ? `Kommentar zu: ${bezug}` : "Kommentar") : "",
        auszug: klartext,
        ...(n.author ? { autorKey: n.author } : {}),
        // Beim Kommentar zählt der BEZUG mehr als der Resttext: „wozu gehört
        // das" ist die Frage, mit der man im Archiv nach ihm sucht.
        unterzeile: alsKommentar
            ? bezug
                ? `Kommentar zu: ${bezug}`
                : "Kommentar"
            : rest || "Notiz",
        wann: archivDatum(at),
        // `author` ist der Benutzerschlüssel, kein Name — er steht nur im
        // Suchtext, nicht in der Anzeige (das Auflösen bräuchte die
        // Benutzerliste und damit React-Kontext).
        suchtext: [klartext, n.author, n.todoTitel, bezug].filter(Boolean).join(" "),
        todoId: n.todoId ?? null,
        todoTitel: n.todoTitel ?? null,
        notiz: n,
    };
}

/**
 * Alle Arten in EINER Liste, neueste zuerst.
 *
 * Mail, Anruf, Dokument und Termin kommen über {@link ereignisseAus} — dieselbe
 * Umformung, die auch die Karten im Notizstrom speist. So steht in der
 * Archivzeile wörtlich, was auf der Karte steht; zwei Beschreibungen desselben
 * Ereignisses liefen sonst auseinander.
 *
 * `kunde` ist der Name, der bei einem Dokument in der Kopfzeile steht — ein
 * Dokument hat keinen Absender.
 */
export function archivZeilen(quellen: ArchivQuellen, kunde: string | null): ArchivZeile[] {
    const proKey = new Map(quellen.eintraege.map((e) => [e.key, e]));
    const todoTitel = new Map((quellen.todos ?? []).map((t) => [t.id, t.titel]));

    const out: ArchivZeile[] = [];
    /**
     * Titel je Kommentar-Ziel (`mail:7`, `notiz:vorgang:3`, …) — damit die
     * Kommentarzeile sagen kann, WOZU sie gehört. Sie wird immer gefüllt: das
     * kostet eine Map über Einträge und Notizen und spart eine zweite
     * Verzweigung im Rumpf.
     */
    const titelJeZiel = new Map<string, string>();
    for (const n of quellen.notizen) {
        const klartext = notizKlartext(n.text, n.format);
        if (!klartext) continue;
        titelJeZiel.set(`notiz:${n.source ?? "vorgang"}:${n.id}`, kuerze(klartext, 60));
    }

    /**
     * Ein Anruf an mehreren To-dos steht im Verlauf je To-do einmal (PO
     * 01.10.2026) — im Archiv nur EINMAL, mit allen To-dos als Herkunft.
     */
    const anrufZeilen = new Map<number, ArchivZeile>();
    for (const ev of ereignisseAus(quellen.eintraege, kunde)) {
        const e = proKey.get(ev.key);
        const tid = e?.todoId ?? null;
        const schonDa = ev.anruf ? anrufZeilen.get(ev.anruf.id) : undefined;
        if (schonDa) {
            const titel = tid != null ? (todoTitel.get(tid) ?? null) : null;
            if (titel && !(schonDa.todoTitel ?? "").split(" · ").includes(titel)) {
                schonDa.todoTitel = schonDa.todoTitel ? `${schonDa.todoTitel} · ${titel}` : titel;
            }
            if (schonDa.todoId == null && tid != null) schonDa.todoId = tid;
            continue;
        }
        // Was in der Karte zusammengefaltet steht, bleibt findbar — wer nach
        // dem Betreff einer älteren Mail der Unterhaltung sucht, landet hier.
        const gefaltet = [
            ...(e?.unterhaltung ?? []).flatMap((m) => [m.subject, m.senderName, m.senderEmail, m.preview]),
            ...(e?.fassungen ?? []).map((d) => d.fileName),
        ];
        const extra =
            ev.mail
                ? [ev.mail.senderEmail, ev.mail.recipients]
                : ev.dokument
                  ? [ev.dokument.category]
                  : ev.anruf
                    ? [ev.anruf.fromNumber, ev.anruf.toNumber, ev.anruf.userName]
                    : ev.termin
                      // Der ganze Notiztext ist Suchgut — auf der Karte steht
                      // nur der Auszug, im Archiv soll man auch das finden,
                      // was dort abgeschnitten ist.
                      ? [ev.termin.mailbox, htmlZuKlartext(ev.termin.notizSeite?.contentHtml ?? "")]
                      : [];
        // Kommentare gibt es an Mail, Anruf und Dokument — nicht am Termin
        // (kein Bezugsfeld an der Notiz); er bleibt aus `titelJeZiel` heraus.
        const zielId = ev.mail?.id ?? ev.anruf?.id ?? ev.dokument?.id;
        // Die Kennung (Betreff der Mail, Richtung·Status·Dauer des Anrufs,
        // Dateiname·Typ·Größe des Dokuments) — seit dem 14.09.2026 über
        // `ereignisKennung`, weil der Dateiname im Kopf der Karte nicht mehr
        // steht und das Archiv ihn trotzdem als Titel braucht.
        const kennung = ereignisKennung(ev);
        const zielArt = ev.dokument ? "dokument" : ev.art;
        if (zielId != null) titelJeZiel.set(`${zielArt}:${zielId}`, kennung);
        const zeile: ArchivZeile = {
            key: ev.key,
            art: ev.art,
            at: ev.zeit,
            // Die Kennung ist der Titel, `wer` die Gegenseite (Absender,
            // Gesprächspartner, Kunde) — dieselbe Aufteilung, die seit dem
            // 13.09.2026 im Kopf der Ereigniskarte steht. Der Inhalt bleibt
            // der Karte; im Archiv ist er Suchgut, keine dritte Zeile.
            titel: kennung,
            unterzeile: ev.wer,
            wann: archivDatum(ev.zeit),
            // Die Karte: `kopfTitel` statt `kennung` — beim Dokument ist das
            // Typ·Größe ohne den Namen, der in der Karte selbst steht.
            wer: ev.wer,
            kennung: ev.kopfTitel,
            ...(ev.inhalt ? { auszug: ev.inhalt } : {}),
            suchtext: [ev.kopfTitel, ev.wer, ev.inhalt, ...extra, ...gefaltet]
                .filter(Boolean)
                .join(" "),
            todoId: tid,
            todoTitel: tid != null ? (todoTitel.get(tid) ?? null) : null,
            ...(ev.mail ? { mail: ev.mail } : {}),
            ...(ev.mail && e?.unterhaltung?.length ? { unterhaltung: e.unterhaltung } : {}),
            ...(ev.anruf ? { anruf: ev.anruf } : {}),
            ...(ev.dokument ? { dokument: ev.dokument } : {}),
            ...(ev.dokument && e?.fassungen?.length ? { fassungen: e.fassungen } : {}),
            ...(ev.termin ? { termin: ev.termin } : {}),
        };
        out.push(zeile);
        if (ev.anruf) anrufZeilen.set(ev.anruf.id, zeile);
    }

    // INHALTSLEERE Notizen sind keine Einträge (PO 13.09.2026: „leergeräumt
    // heißt gelöscht"). Sonst zählt das Archiv im Kopf „2 Einträge" und am
    // Chip „Notizen 2", wo eine einzige Notiz steht — und die zweite Zeile
    // ließe sich nicht einmal anklicken, weil an ihr nichts dranhängt. Neue
    // entstehen nicht mehr; was vor der Entscheidung entstand, liegt noch in
    // der Datenbank.
    for (const n of quellen.notizen) {
        if (!notizKlartext(n.text, n.format)) continue;
        if (quellen.ohneNotizen && !(quellen.mitKommentaren && istKommentar(n))) continue;
        out.push(
            ausNotiz(n, {
                ...(quellen.mitKommentaren ? { mitKommentaren: true } : {}),
                titelJeZiel,
            }),
        );
    }

    // Gleichstand deterministisch über den Schlüssel auflösen — sonst tanzen
    // gleichzeitige Zeilen bei jedem Rendern (wie in `baueVerlauf`).
    return out.sort((a, b) => b.at.getTime() - a.at.getTime() || a.key.localeCompare(b.key));
}

/**
 * Mail-Entwürfe als Verlaufszeilen (User 22.10.2026) — wie eine Mail, nur
 * mit Empfänger statt Absender im Kopf. Einsortiert wird nach dem letzten
 * Speichern: Der Verlauf steht neueste zuerst, ein frisch gespeicherter
 * Entwurf also oben — dort, wo die Mail nach dem Senden stünde.
 */
export function entwurfZeilen(
    entwuerfe: ReadonlyArray<VorgangEntwurf>,
    todoId: number | null = null,
): ArchivZeile[] {
    return entwuerfe.map((e) => {
        const at = zeit(e.lastModifiedDateTime);
        const betreff = e.subject || "(kein Betreff)";
        const an = e.to.length ? `an ${e.to.join(", ")}` : "noch ohne Empfänger";
        const anriss = (e.preview ?? "").replace(/\s+/g, " ").trim();
        return {
            key: `entwurf-${e.draftId}`,
            art: "mail" as const,
            at,
            titel: betreff,
            unterzeile: `Entwurf ${an}`,
            wann: archivDatum(at),
            wer: an,
            kennung: betreff,
            ...(anriss ? { auszug: anriss } : {}),
            suchtext: ["Entwurf", betreff, an, anriss, e.mailbox].filter(Boolean).join(" "),
            todoId: e.todoId ?? todoId,
            todoTitel: null,
            entwurf: e,
        };
    });
}

/** Verlaufszeilen plus Entwurfszeilen, in der Reihenfolge von {@link archivZeilen}. */
export function mitEntwurfZeilen(zeilen: ArchivZeile[], entwuerfe: ArchivZeile[]): ArchivZeile[] {
    if (entwuerfe.length === 0) return zeilen;
    return [...zeilen, ...entwuerfe].sort(
        (a, b) => b.at.getTime() - a.at.getTime() || a.key.localeCompare(b.key),
    );
}

/** Wie viele Zeilen je Art — die Zahlen an den Filterchips. */
export function archivZaehlung(zeilen: ArchivZeile[]): Record<ArchivArt, number> {
    const out: Record<ArchivArt, number> = {
        mail: 0,
        anruf: 0,
        dokument: 0,
        termin: 0,
        notiz: 0,
        kommentar: 0,
    };
    for (const z of zeilen) out[z.art] += 1;
    return out;
}
